// ReleasesPage.tsx — one page for "what is released, and what could be".
//
// The default view is the family release board (see releaseBoard/): every
// deliverable of a family as a lane of stages, what each stage runs on each of
// its targets, and how much of the family's work has reached customers. The
// per-repository view is reached with `?repo=owner/name`, from the "Per
// repository" link, or shown under the note when the board cannot be:
//
//   1. What runs: one row per CONFIGURED environment (the live commit and
//      release, who deployed it and when, the rollback target, a failed or
//      running newer attempt, and how many merged pull requests main has that
//      the environment does not). A configured environment with nothing live
//      folds behind "other environments"; one never deployed to is named in a
//      line and given no row.
//   2. Ready to pin: merged in a dependency, not yet in the deploy repo's pin.
//
// A staged release waiting for its deploy command is a row on Needs you, which
// the Releases nav badge counts, not a banner or a strip here: what needs a
// person is listed in one place, so the same release is never shown twice.
//
// This page is about ENVIRONMENTS: what each one runs and what is holding the
// next release. How far an individual change has got — opened, checked,
// reviewed, merged, and out to dev, canary, stable and production — is the Pull
// requests timeline's job, and the two link to each other rather than each
// keeping half a list of pull requests.
//
// Its scope comes from `?repo=owner/name` (default the jeryu deploy repo) or
// `?view=repositories&family=<family>`. The board's address is
// `/releases/family/<family>` (lanes are `#lane-<id>` on it); the older
// `/releases?family=<family>` redirects there (see ReleasesRoute). `/unreleased`
// was a page, then a section here; it is neither now, and its path lands here
// through the moved-path table (`app/movedRoutes.tsx`) because the forge's own
// attention items still link to it.

import { useQueryClient } from '@tanstack/react-query';
import { Link, Navigate, useLocation, useParams, useSearchParams } from 'react-router-dom';

import { sameFamily } from '../components/family/familyScope';
import { useFamilyScope } from '../components/family/FamilyScopeProvider';
import { PageHeader } from '../components/pageHeader';
import { pillClass, type Tone } from '../components/tone/tone';
import { EmptyState, ErrorState, LoadingState } from '../components/state';
import { useAuth } from '../hooks/useAuth';
import { usePins } from '../hooks/usePipeline';

import { RELEASE_BOARD_KEY, useReleaseBoardList } from '../hooks/useReleaseBoard';
import { useReleaseOverview } from '../hooks/useReleaseOverview';
import { useForgeHost } from '../hooks/useForgeHost';
import { useRepositories } from '../hooks/useRepositories';
import { behindPinLines } from './pinsModel';
import { ReadyToPin } from './ReadyToPin';
import { canonicalBoardRedirect } from './releaseBoard/links';
import { ReleaseBoardView } from './releaseBoard/ReleaseBoardView';
import {
  attemptLabel,
  behindLabel,
  releasePullHref,
  releasesHref,
  releaseScopeOptions,
  scopeParams,
  splitEnvironments,
  timelineHref,
  workHref,
  type DeployedRef,
  type EnvironmentRow,
} from './releasesModel';

import { usePageTitle } from '../hooks/usePageTitle';

import './page.css';
import './ReleasesPage.css';
import { When } from '../format/When';

const STATE_TONE: Record<string, Tone> = {
  success: 'ok',
  inactive: 'unknown',
  failure: 'failed',
  error: 'failed',
  in_progress: 'warn',
  queued: 'warn',
  pending: 'warn',
};

/**
 * The `/releases` route: the page, unless the URL is the board's older
 * spelling `?family=<family>`, which is replaced by `/releases/family/<family>`
 * with every other parameter and the hash kept.
 */
export function ReleasesRoute(): JSX.Element {
  const { search, hash } = useLocation();
  const target = canonicalBoardRedirect(search, hash);
  return target ? <Navigate to={target} replace /> : <ReleasesPage />;
}

export function ReleasesPage(): JSX.Element {
  const [params] = useSearchParams();
  const { family: pathFamily } = useParams();
  const scope = useFamilyScope();
  const perRepository = params.has('repo') || params.get('view') === REPOSITORY_VIEW;
  // On the board the family comes from the path; `?family=` still works when
  // the page is mounted without the redirect in front of it, and an address
  // that names none draws the board of the family the shell is scoped to.
  const boardFamily =
    pathFamily ?? (perRepository ? null : params.get('family') ?? (scope.family || null));
  usePageTitle(boardFamily ? `Releases · ${boardFamily}` : 'Releases');
  const repoScope = perRepository ? scopeFrom(params, scope.family) : DEFAULT_SCOPE;
  const repositoryView = <RepositoryReleases scope={repoScope} />;
  // The board's own read, so the freshness stamp says how old the board on
  // screen is without a request of its own. The per-repository view is many
  // reads (environments, compares) with no one age, so it carries no stamp
  // rather than one borrowed from a read it does not show.
  const { user } = useAuth();
  const admin = user?.role === 'admin';
  const boards = useReleaseBoardList(admin && !perRepository);
  const queryClient = useQueryClient();
  const freshness = perRepository
    ? undefined
    : {
        updatedAt: boards.dataUpdatedAt || null,
        isFetching: boards.isFetching,
        // Every board read, not just the list: the lanes below are the page.
        onRefresh: () => {
          void queryClient.invalidateQueries({ queryKey: RELEASE_BOARD_KEY });
        },
      };

  return (
    <div className="page page--wide" data-testid="releases-page">
      <PageHeader
        title={boardFamily ?? 'Releases'}
        testId="releases-header"
        parent={boardFamily ? 'nav.releases' : undefined}
        crumbs={boardFamily ? [{ label: boardFamily }] : undefined}
        freshness={freshness}
      >
        <nav className="releases__views" aria-label="Releases view">
          <Link
            className="releases__view"
            to="/releases"
            aria-current={perRepository ? undefined : 'page'}
            data-testid="releases-view-board"
          >
            Family board
          </Link>
          <Link
            className="releases__view"
            to={REPOSITORY_VIEW_PATH}
            aria-current={perRepository ? 'page' : undefined}
            data-testid="releases-view-repository"
          >
            Per repository
          </Link>
        </nav>
      </PageHeader>

      {perRepository ? (
        repositoryView
      ) : (
        <ReleaseBoardView family={boardFamily} fallback={repositoryView} />
      )}
    </div>
  );
}

/** `?view=repositories` keeps the per-repository view when it is scoped to a family. */
const REPOSITORY_VIEW = 'repositories';

/**
 * The per-repository view with no repository named: the view picks the first
 * deploy repository the forge reports, so no install's own repo is a default.
 */
const REPOSITORY_VIEW_PATH = `/releases?view=${REPOSITORY_VIEW}`;

interface RepositoryScope {
  repo: string | null;
  family: string | null;
  branch: string;
}

const DEFAULT_SCOPE: RepositoryScope = { repo: null, family: null, branch: 'main' };

/**
 * The per-repository view's scope. On the board `?family=` picks the board;
 * here it scopes to every repository of the family, which
 * `?view=repositories&family=<name>` asks for — and so does
 * `?view=repositories` under a shell-wide family scope, which that address is
 * the per-repository view of.
 */
function scopeFrom(params: URLSearchParams, scoped: string): RepositoryScope {
  const family =
    params.get('view') === REPOSITORY_VIEW ? params.get('family') ?? (scoped || null) : null;
  return {
    family,
    repo: family ? null : params.get('repo'),
    branch: params.get('branch') ?? 'main',
  };
}

/**
 * The per-repository view: what each environment of one repository runs, the
 * staged release, and what its dependencies merged that its pins lack.
 */
function RepositoryReleases({ scope }: { scope: RepositoryScope }): JSX.Element {
  const [, setParams] = useSearchParams();
  const familyScope = useFamilyScope();
  const { user } = useAuth();
  const { family, branch } = scope;
  // The deploy repos and families the forge knows feed the scope select, and
  // the first of them is the repository this view opens on when the URL names
  // none: which repositories exist is the server's to say.
  const pins = usePins(user?.role === 'admin');
  const repoId = scope.repo ?? pins.data?.consumers[0]?.repo ?? null;

  // Every repository, narrowed here rather than in the query: the scope is one
  // key per family (`acme`) where this list may spell it `acme-split`.
  const members = useRepositories({ sort: 'name' }, { enabled: family !== null });
  let repos: { id: string; branch: string }[] = [];
  if (repoId) {
    repos = [{ id: repoId, branch }];
  } else if (members.data) {
    repos = members.data.repositories
      .filter((r) => sameFamily(r.family, family))
      .map((r) => ({
        id: `${r.id.owner}/${r.id.name}`,
        branch: r.default_branch || 'main',
      }));
  }

  const options = releaseScopeOptions({ repo: repoId, family }, pins.data?.consumers ?? []);
  const scopeValue = family ? `family:${family}` : repoId ? `repo:${repoId}` : '';

  const setScope = (value: string): void => {
    const next = scopeParams(value);
    if (!next) return;
    if ('family' in next) {
      // Scoping this view to a family scopes the shell to it.
      familyScope.setFamily(next.family, { to: `/releases?view=${REPOSITORY_VIEW}` });
      return;
    }
    setParams(next);
  };

  return (
    <>
      <section className="page__section" aria-labelledby="releases-repository">
        <h2 className="page__section-title" id="releases-repository">
          Per repository
        </h2>
        <p className="releases__muted">
          For how far one change has got on its way here, see the{' '}
          <Link to={timelineHref({ repo: repoId, family })}>In flight timeline</Link>; for
          work not yet on a pull request, see <Link to={workHref({ repo: repoId, family })}>Work</Link>.
        </p>
        <p className="releases__repo">
          <label htmlFor="releases-scope">Repository or family</label>
          <select
            id="releases-scope"
            value={scopeValue}
            onChange={(event) => setScope(event.currentTarget.value)}
            data-testid="releases-scope"
          >
            {/* Nothing chosen and nothing reported: the select says so rather
                than standing on a repository this install may not have. */}
            {scopeValue === '' ? (
              <option value="">No repository reported yet</option>
            ) : null}
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </p>
      </section>

      {repoId ? <Environments repoId={repoId} branch={branch} /> : null}

      <ReadyToPin
        scope={{
          repo: repoId,
          family,
          familyRepos: family ? repos.map((repo) => repo.id) : [],
        }}
      />
    </>
  );
}

function Environments({ repoId, branch }: { repoId: string; branch: string }): JSX.Element {
  const { rows, isLoading, error, refetch } = useReleaseOverview(repoId, branch);
  const anyDeployed = rows.some((row) => row.configured);
  const { live, quiet, undeployed } = splitEnvironments(rows);
  return (
    <section className="page__section" aria-labelledby="releases-environments">
      <h2 className="page__section-title" id="releases-environments">
        What runs
      </h2>
      <UnpinnedLine repoId={repoId} />
      {error ? (
        <ErrorState
          title={`Deployment history is unavailable for ${repoId}.`}
          error={error}
          onRetry={refetch}
          testId="releases-error"
        />
      ) : isLoading ? (
        <LoadingState variant="message" title="Loading environments…" />
      ) : (
        <>
          {!anyDeployed ? (
            <EmptyState
              title={`No deployment of ${repoId} has been recorded yet.`}
              description="Deploys record themselves here from the next release onward."
              testId="releases-empty"
            />
          ) : null}
          {live.length > 0 ? <EnvironmentTable rows={live} repoId={repoId} branch={branch} /> : null}
          {quiet.length > 0 ? (
            <details className="releases__other" data-testid="releases-other-environments">
              <summary>
                {quiet.length} other environment{quiet.length === 1 ? '' : 's'} with nothing
                live ({quiet.map((row) => row.name).join(', ')})
              </summary>
              <EnvironmentTable rows={quiet} repoId={repoId} branch={branch} />
            </details>
          ) : null}
          {undeployed.length > 0 ? (
            // Never deployed to: a row of empty cells would only repeat the
            // names, so the names are all this says.
            <p className="releases__muted" data-testid="releases-undeployed-environments">
              Never deployed to: {undeployed.map((row) => row.name).join(', ')}.
            </p>
          ) : null}
        </>
      )}
    </section>
  );
}

function EnvironmentTable({
  rows,
  repoId,
  branch,
}: {
  rows: EnvironmentRow[];
  repoId: string;
  branch: string;
}): JSX.Element {
  return (
    <div className="table-scroll">
      <table className="releases__table" data-testid="releases-table">
        <thead>
          <tr>
            <th scope="col">Environment</th>
            <th scope="col">Running</th>
            <th scope="col">Deployed</th>
            <th scope="col">Behind {branch}</th>
            <th scope="col">Rollback target</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <EnvironmentRowView key={row.name} row={row} repoId={repoId} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function EnvironmentRowView({ row, repoId }: { row: EnvironmentRow; repoId: string }): JSX.Element {
  const forgeHost = useForgeHost();
  if (!row.current) {
    // Configured, nothing live: the environments never deployed to are named
    // in a line instead (see splitEnvironments), so they reach no table.
    return (
      <tr className="releases__row releases__row--empty" data-testid={`releases-env-${row.name}`}>
        <th scope="row">{row.name}</th>
        <td colSpan={4}>
          <span>
            nothing live yet
            {row.pendingAttempt ? <> · <Attempt attempt={row.pendingAttempt} /></> : null}
          </span>
        </td>
      </tr>
    );
  }
  const behind = behindLabel(row);
  return (
    <tr className="releases__row" data-testid={`releases-env-${row.name}`}>
      <th scope="row">
        {row.name}
        {row.url ? (
          <a className="releases__url" href={row.url} rel="noreferrer">
            open
          </a>
        ) : null}
      </th>
      <td>
        <Ref ref_={row.current} />
        {row.pendingAttempt ? (
          <div>
            <Attempt attempt={row.pendingAttempt} />
          </div>
        ) : null}
      </td>
      <td>
        <When at={row.current.deployedAt} />{' '}
        by {row.current.deployedBy}
      </td>
      <td data-testid={`releases-behind-${row.name}`}>
        {behind === null ? (
          <span className="releases__muted">unknown</span>
        ) : (
          <details>
            <summary className={row.commitsBehind ? 'releases__behind' : undefined}>{behind}</summary>
            {row.unshipped && row.unshipped.length > 0 ? (
              <ul className="releases__prs">
                {row.unshipped.map((pr) => (
                  <li key={pr.number}>
                    <Link to={releasePullHref(forgeHost(repoId), repoId, pr.number)}>
                      #{pr.number} {pr.title}
                    </Link>{' '}
                    <span className="releases__muted">· {pr.author}</span>
                  </li>
                ))}
              </ul>
            ) : null}
          </details>
        )}
      </td>
      <td>{row.previous ? <Ref ref_={row.previous} /> : <span className="releases__muted">none</span>}</td>
    </tr>
  );
}

function Ref({ ref_ }: { ref_: DeployedRef }): JSX.Element {
  return (
    <span className="releases__ref">
      <code title={ref_.sha}>{ref_.shortSha}</code>
      {ref_.release ? <span className="releases__release">{ref_.release}</span> : null}
    </span>
  );
}

function Attempt({ attempt }: { attempt: DeployedRef }): JSX.Element {
  const label = attemptLabel(attempt);
  return (
    <>
      <span className={pillClass(STATE_TONE[attempt.state] ?? 'unknown')} title={attempt.release ?? attempt.sha}>
        {label.words}
        {label.shortSha ? ` ${label.shortSha}` : ''}
      </span>
      {attempt.logUrl ? (
        <>
          {' '}
          <a href={attempt.logUrl} target="_blank" rel="noreferrer">
            deploy log
          </a>
        </>
      ) : null}
    </>
  );
}

function relative(iso: string): string {
  const seconds = Math.round((Date.now() - Date.parse(iso)) / 1000);
  if (!Number.isFinite(seconds)) return iso;
  if (seconds < 90) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 90) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 36) return `${hours} h ago`;
  return `${Math.round(hours / 24)} days ago`;
}

/**
 * What this repo's release cannot carry yet: a dependency merged work that the
 * pin does not reach. One line, linking to where the commits are listed.
 */
function UnpinnedLine({ repoId }: { repoId: string }): JSX.Element | null {
  const { user } = useAuth();
  const pins = usePins(user?.role === 'admin');
  const lines = behindPinLines(pins.data?.consumers ?? [], repoId);
  if (lines.length === 0) return null;
  return (
    <p className="releases__muted" role="status" data-testid="releases-unpinned">
      {lines.join('; ')}.{' '}
      <Link to={timelineHref({ repo: repoId })}>See what a bump would ship</Link>
    </p>
  );
}
